// Quem pode alterar os dados cadastrais de uma UC (#6239, 27/10/2026).
//
// Pedido do Marcus Vinicius (reunião de 08/10/2026), decidido pelo Felipe em
// 27/10/2026: só administrador e o Financeiro (Dhaiane, João Henrique e Bruna,
// pela permissão abaixo) alteram nome, CPF/CNPJ, endereço, contatos, número da
// UC e pagador de uma UC que já está em operação. O Faturamento pede a correção
// ao Financeiro.
//
// A trava de verdade está no banco (gatilho `trg_protege_dados_cadastrais_uc`,
// migration 20261027120000). Esta regra só espelha a do banco para a tela
// travar os campos antes de a pessoa tentar salvar.
//
// UC ainda na venda (cadastro não aprovado e sem situação) fica livre: é o
// Comercial montando o cadastro antes da análise cadastral.
//
// Sem imports de runtime: roda sob `node --test`.

export const EDITAR_CADASTRO_PERMISSION = 'Editar dados cadastrais';

/** Colunas da UC que só o Financeiro ou um adm altera. Mesma lista do gatilho. */
export const CAMPOS_CADASTRAIS_UC = [
    'numero_uc', 'outro_numero_uc',
    'nome_cliente', 'cpf_cnpj_cliente', 'data_nascimento_cliente',
    'titular_fatura', 'cpf_cnpj_titular', 'data_nascimento_titular',
    'cep', 'logradouro', 'numero', 'complemento', 'bairro', 'cidade', 'estado', 'quadra', 'lote',
    'whatsapp_cliente', 'whatsapp_cliente_2', 'email_cliente', 'email_cliente_2', 'outro_email',
    'pagador_id',
    // `endereco` é o texto antigo montado a partir das partes. O banco não o
    // protege (derivado), mas a tela também não o regrava sem permissão.
    'endereco',
] as const;

export const MENSAGEM_CADASTRO_TRAVADO =
    'Só o Financeiro ou um administrador altera os dados cadastrais desta UC (nome, CPF/CNPJ, endereço, contatos, número da UC e pagador). Se algum estiver errado, peça a correção ao Financeiro.';

type Perfil = { role?: string | null; permissions?: string[] | null } | null | undefined;
type UcSituacao = { cadastro_aprovado?: boolean | null; situacao?: string | null } | null | undefined;

/** Administrador ou quem tem a permissão "Editar dados cadastrais". */
export function podeEditarCadastro(perfil: Perfil): boolean {
    if (!perfil) return false;
    if (perfil.role === 'adm') return true;
    return !!perfil.permissions?.includes(EDITAR_CADASTRO_PERMISSION);
}

/** A UC já está em operação: cadastro aprovado ou com situação definida. */
export function ucComCadastroTravado(uc: UcSituacao): boolean {
    if (!uc) return false;
    return !!uc.cadastro_aprovado || !!(uc.situacao && String(uc.situacao).trim());
}

/** Pode alterar os dados cadastrais DESTA UC. */
export function podeEditarCadastroDaUc(perfil: Perfil, uc: UcSituacao): boolean {
    return podeEditarCadastro(perfil) || !ucComCadastroTravado(uc);
}

/** O mesmo objeto sem as colunas cadastrais — para gravar o resto da UC sem tocar nelas. */
export function semCamposCadastrais<T extends Record<string, unknown>>(payload: T): Partial<T> {
    const copia: Record<string, unknown> = { ...payload };
    for (const campo of CAMPOS_CADASTRAIS_UC) delete copia[campo];
    return copia as Partial<T>;
}

/** O erro veio da trava do banco (gatilho com errcode 42501 e a mensagem dele). */
export function erroDeCadastroTravado(erro: unknown): boolean {
    const e = erro as { code?: string; message?: string } | null | undefined;
    return !!e && e.code === '42501' && /dados cadastrais/i.test(e.message || '');
}
